import { SchoolContentItem } from "@axl-dev/school-sdk";
import { Button, Progress } from "antd";

import { SchoolContentSectionType } from "@app/pages/school-materials/model/school-content-item";
import { useFileProvider } from "@app/shared/lib/hooks";
import {
  GradientPlaceholder,
  Image,
  Loc,
  ValuePlaceholder,
} from "@app/shared/ui";

import FolderBadge from "./Badge/Badge";

type SchoolCourseCardProps = {
  content: SchoolContentItem;
  sectionType: SchoolContentSectionType;
  onOpen: () => void;
  titlePosition?: "onImage" | "belowImage";
};

export const FolderCard: React.FC<SchoolCourseCardProps> = ({
  content,
  sectionType,
  onOpen,
  titlePosition = "onImage",
}) => {
  const fileProvider = useFileProvider();
  const isBelowImage = titlePosition === "belowImage";

  const titleBlock = (
    <div
      className="material-card-title-wrapper"
      title={content.title}
      data-testid="course-folder-title"
    >
      <ValuePlaceholder
        value={content.title}
        className="material-card-title"
      />
    </div>
  );

  return (
    <div
      className="material-card"
      data-testid="course-folder-card"
      data-id={content.id}
      onClick={() => onOpen()}
    >
      <div className="material-card-image" data-testid="course-folder-preview">
        <Image
          src={fileProvider.getUrlByKey(content.coverCloudKey)}
          fit="cover"
          width="100%"
          height="100%"
          placeholder={
            <GradientPlaceholder
              uniq={content.id}
              width="100%"
              height="100%"
              className="material-card-image-placeholder"
            />
          }
        />

        {!isBelowImage && titleBlock}

        <FolderBadge />
      </div>

      <div className="material-card-content">
        {isBelowImage && titleBlock}
        <div className="material-card-progress-courses-count">
          {content.itemCount}&nbsp;
          {sectionType === SchoolContentSectionType.Course ? (
            <Loc.Plural word="Course" number={content.itemCount} case="lower" />
          ) : (
            <Loc.Plural
              word="Library"
              number={content.itemCount}
              case="lower"
            />
          )}
        </div>

        <div
          className="material-card-progress"
          data-testid="course-progress"
          data-progress={Math.round(content.progress ?? 0)}
        >
          <Progress
            percent={Math.round(content.progress ?? 0)}
            size="small"
            status="active"
            className={
              sectionType === SchoolContentSectionType.Library ? "hidden" : ""
            }
          />
        </div>

        <div className="flex flex-col mb-10" data-testid="folder-desc">
          <Loc
            word="Course.descLabel"
            default="Description"
            className="fw-600 secondary-text"
          />
          <div className="material-card-desc">
            <ValuePlaceholder
              value={content.description}
              placeholder="-"
            />
          </div>
        </div>

        <div className="material-card-btns">
          <Button
            className="material-card-main-btn"
            type="primary"
            onClick={(e) => {
              e.stopPropagation();
              onOpen();
            }}
          >
            <Loc word="Open" />
          </Button>
        </div>
      </div>
    </div>
  );
};
